import type { Category } from "../types";
import { nativeCookieDescriptor, readCookieJar } from "../core/native";
import { log, type BlockingContext } from "./context";

export interface CookiePattern {
  pattern: string;
  category: Category;
}

/**
 * § 25 TDDDG is about device access generally, not only about network requests: a script
 * that is already running must still not write a tracking cookie. This wraps the
 * `document.cookie` setter and drops writes whose name belongs to a category that has not
 * been granted.
 *
 * The library's own cookie is written through the captured native setter (core/native.ts),
 * so it is never caught by this guard.
 */
export function installStorageGuard(context: BlockingContext, patterns: CookiePattern[]): void {
  const descriptor = nativeCookieDescriptor;
  if (!descriptor?.set || !descriptor.get || patterns.length === 0) return;
  const nativeSet = descriptor.set;
  const nativeGet = descriptor.get;

  Object.defineProperty(Document.prototype, "cookie", {
    configurable: true,
    enumerable: descriptor.enumerable ?? true,
    get(this: Document): string {
      return String(nativeGet.call(this));
    },
    set(this: Document, value: string) {
      const name = value.split("=")[0]?.trim() ?? "";
      const blocked = patterns.find(
        ({ pattern, category }) => matches(name, pattern) && !context.isGranted(category),
      );
      if (blocked) {
        log(context, `blocked cookie "${name}" (${blocked.category})`);
        return;
      }
      nativeSet.call(this, value);
    },
  });
}

/** Removes cookies belonging to categories that are (no longer) granted. */
export function purgeCookies(patterns: CookiePattern[], isGranted: (c: Category) => boolean): string[] {
  const jar = readCookieJar();
  if (!jar) return [];

  const revoked = patterns.filter(({ category }) => !isGranted(category)).map((p) => p.pattern);
  return jar
    .split(";")
    .map((pair) => pair.split("=")[0]?.trim() ?? "")
    .filter((name) => name && revoked.some((pattern) => matches(name, pattern)));
}

function matches(name: string, pattern: string): boolean {
  if (pattern.endsWith("*")) return name.startsWith(pattern.slice(0, -1));
  return name === pattern;
}
